import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, SlidersHorizontal, MoreVertical, ChevronDown,
  Package, Star, Edit3, Trash2, Snowflake, Thermometer, Sun,
  AlertTriangle, ScanBarcode,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  listenToHostProducts,
  deleteProduct,
  Product,
} from "../../../redux/productSlice";
import { fetchAllProductSummaries } from "../../../redux/productRatingSlice";

import { listenToCommerceData } from "../../../redux/commercePartnersSlice";

/* ═══════════════════════════════════════════════════════════
   GROCERY ONLY — scope this page's data to the grocery branch of
   the category tree and the grocery unit attribute, same source
   the AddGrocery form writes from.
═══════════════════════════════════════════════════════════ */
import { groceryCategories, groceryUnits } from "../../../CATEGORIES/GroceryCategories";

const GROCERY_CATEGORY_IDS = new Set(groceryCategories.map((c: any) => c.id));
const UNIT_ATTRIBUTE_ID = 90201; // matches AddGrocery's unitAttribute.id

// Matches PartnerCategories.businessType — "Grocery Stores" id:1.
const GROCERY_STORE_CATEGORY_ID = 1;

/* ═══════════════════════════════════════════════════════════
   TOKENS — same shopfront palette as GroceriesHome / AddGrocery,
   so a seller never feels like they left the grocery product.
═══════════════════════════════════════════════════════════ */
const T = {
  bg:         "#F7F6F2",
  cardBg:     "#FFFFFF",
  surface:    "#EEF5E8",
  offwhite:   "#FBF9F4",

  ink:        "#14180F",
  inkMid:     "#2E3828",
  muted:      "#727868",
  mutedFaint: "#A8AC9E",

  border:     "rgba(50,60,30,0.12)",
  divider:    "rgba(50,60,30,0.08)",

  green:      "#264D0E",
  greenMid:   "#3D7022",
  greenLight: "#6FA845",
  greenPale:  "#EEF5E8",

  saffron:    "#D97706",
  saffronPale:"#FBF0DD",

  red:        "#C0392B",
  redPale:    "#FBE9E6",
};

const GRAD      = `linear-gradient(90deg, ${T.green} 0%, ${T.greenMid} 40%, ${T.saffron} 75%, #C2650A 100%)`;
const GRAD_CTA  = `linear-gradient(135deg, ${T.greenMid} 0%, ${T.saffron} 100%)`;
const GRAD_SIZE = "260% 100%";

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=Space+Mono:wght@400;700&display=swap');
  *,*::before,*::after { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 8px; }
  input, button { font-family: inherit; }
  body { background: ${T.bg}; }

  @keyframes arSpin { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
  @keyframes pulseDot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.4)} }
  @keyframes fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  input::placeholder { color: ${T.mutedFaint}; }
  input:focus { outline: none; }

  .gp-search { transition: border-color .15s, box-shadow .15s; }
  .gp-search:focus-within { border-color: rgba(61,112,34,.5) !important; box-shadow: 0 0 0 3px rgba(61,112,34,.09); }

  .gp-filter { transition: background .15s, border-color .15s; }
  .gp-filter:hover { background: ${T.greenPale} !important; border-color: rgba(61,112,34,.4) !important; }

  .gp-add { transition: transform .15s, box-shadow .15s, filter .15s; }
  .gp-add:hover { transform: translateY(-1px); filter: brightness(1.04); }

  .gp-row { transition: box-shadow .2s, transform .15s, border-color .2s; cursor: pointer; }
  .gp-row:hover { box-shadow: 0 8px 28px rgba(38,77,14,0.10); border-color: rgba(61,112,34,.4) !important; transform: translateY(-2px); }

  .gp-menu { position: absolute; right: 12px; top: 52px; background: #fff; border: 1.5px solid ${T.border};
             border-radius: 14px; overflow: hidden; z-index: 50; box-shadow: 0 14px 40px rgba(20,24,15,.16); min-width: 168px; }
  .gp-menu-item { display: flex; align-items: center; gap: 10px; padding: 11px 16px; font-size: 13px; font-weight: 600;
                  color: ${T.ink}; cursor: pointer; border: none; background: none; width: 100%; text-align: left;
                  transition: background .15s; }
  .gp-menu-item:hover { background: ${T.greenPale}; }
  .gp-menu-item.danger { color: ${T.red}; }
  .gp-menu-item.danger:hover { background: ${T.redPale}; }

  .gp-stat { transition: border-color .15s, box-shadow .15s; }
  .gp-stat:hover { border-color: rgba(61,112,34,.3); box-shadow: 0 4px 16px rgba(20,24,15,.06); }
  .gp-mono { font-family: 'Space Mono', monospace; }

  .gp-toolstoggle { transition: background .15s, border-color .15s, transform .2s; }
  .gp-toolstoggle:hover { background: ${T.greenPale} !important; border-color: rgba(61,112,34,.4) !important; }
`;

/* ─── helpers ─── */
const fmtPrice = (n: number) => `€${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** "Fresh Basket Market" -> "FB", "greens" -> "G" */
const getInitials = (name?: string | null) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const STORAGE_META: Record<string, { name: string; icon: any; color: string }> = {
  ambient: { name: "Dry",    icon: Sun,         color: T.saffron },
  chilled: { name: "Fridge", icon: Thermometer, color: T.greenMid },
  frozen:  { name: "Frozen", icon: Snowflake,   color: "#2E7BB0" },
};

/** Colour + label for how much shelf life is left, from a groceryDetails.expirationDate string. */
function shelfLifeState(expirationDate?: string): { pct: number; color: string; label: string } | null {
  if (!expirationDate) return null;
  const exp = new Date(expirationDate).getTime();
  if (Number.isNaN(exp)) return null;
  const daysLeft = Math.ceil((exp - Date.now()) / 86400000);
  if (daysLeft < 0) return { pct: 100, color: T.red, label: "Expired" };
  if (daysLeft <= 3) return { pct: 92, color: T.red, label: `${daysLeft}d left` };
  if (daysLeft <= 14) return { pct: 60, color: T.saffron, label: `${daysLeft}d left` };
  return { pct: 22, color: T.greenMid, label: `${daysLeft}d left` };
}

/* ─── BrandAvatar — logo image, falls back to initials on missing/broken src ─── */
const BrandAvatar: React.FC<{ name?: string | null; logo?: string | null; size?: number }> = ({ name, logo, size = 44 }) => {
  const [broken, setBroken] = useState(false);
  const showImg = !!logo && !broken;

  return (
    <div style={{
      width: size, height: size, borderRadius: size * 0.32, flexShrink: 0, overflow: "hidden",
      background: showImg ? T.offwhite : GRAD_CTA,
      border: `1.5px solid ${T.border}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 14px rgba(20,24,15,0.08)",
    }}>
      {showImg ? (
        <img
          src={logo as string}
          alt={name || "Brand"}
          onError={() => setBroken(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <span style={{
          fontFamily: "'Syne',sans-serif", fontWeight: 800, color: "#fff",
          fontSize: size * 0.36, letterSpacing: -0.2,
        }}>
          {getInitials(name)}
        </span>
      )}
    </div>
  );
};

/* ─── GroceryRow ─── */
interface RowCardProps {
  product:  Product;
  rating?:  { avg: number; count: number } | null;
  onEdit:   () => void;
  onDelete: () => void;
}

const GroceryRow: React.FC<RowCardProps> = ({ product, rating, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const thumb   = product.photos?.[0] ?? null;
  const details: any = (product as any).groceryDetails ?? {};
  const attrs:   any = (product as any).attributes ?? {};

  const unitName = (groceryUnits as any[]).find((u) => u.id === attrs[UNIT_ATTRIBUTE_ID])?.name;
  const stock = product.stock ?? 0;
  const reorderThreshold = details.reorderThreshold ?? 0;
  const isLowStock = reorderThreshold > 0 && stock <= reorderThreshold;
  const storage = details.storageRequirement ? STORAGE_META[details.storageRequirement] : null;
  const shelf = shelfLifeState(details.expirationDate);

  return (
    <motion.div
      className="gp-row"
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      style={{
        position: "relative", background: T.cardBg, border: `1.5px solid ${T.border}`,
        borderRadius: 16, marginBottom: 12, display: "flex", alignItems: "center", gap: 14,
        padding: 12, overflow: "visible", boxShadow: "0 2px 12px rgba(20,24,15,0.04)",
      }}
      onClick={onEdit}
    >
      {/* Left gradient accent bar */}
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: 3, borderRadius: "16px 0 0 16px",
        background: GRAD, backgroundSize: GRAD_SIZE, animation: "arSpin 5s linear infinite",
      }} />

      {/* Thumbnail */}
      <div style={{
        width: 88, height: 88, borderRadius: 12, overflow: "hidden",
        background: `linear-gradient(135deg, ${T.greenPale}, #F3F8EE)`,
        flexShrink: 0, position: "relative", marginLeft: 8, border: `1px solid ${T.border}`,
      }}>
        {thumb ? (
          <img src={thumb} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Package size={28} color={T.greenMid} style={{ opacity: 0.55 }} />
          </div>
        )}
        {(product as any).sold > 0 && (
          <div style={{ position: "absolute", bottom: 5, left: 5, background: "rgba(20,24,15,0.78)", backdropFilter: "blur(6px)",
                        borderRadius: 6, padding: "2px 7px", display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: T.saffron, animation: "pulseDot 2s infinite" }} />
            <span style={{ fontSize: 9, fontWeight: 700, color: "#fff" }}>
              {(product as any).sold >= 1000 ? `${((product as any).sold / 1000).toFixed(0)}K` : (product as any).sold} sold
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 2 }}>
          <span style={{ fontWeight: 800, fontSize: 15, color: T.ink, overflow: "hidden", textOverflow: "ellipsis",
                          whiteSpace: "nowrap", letterSpacing: -0.2, fontFamily: "'Syne',sans-serif" }}>
            {product.name || "Untitled item"}
          </span>
          {details.brand && (
            <span style={{ fontSize: 11, color: T.muted, fontWeight: 600, flexShrink: 0 }}>{details.brand}</span>
          )}
        </div>

        {details.sku && (
          <div className="gp-mono" style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10.5, color: T.mutedFaint, marginBottom: 7 }}>
            <ScanBarcode size={11} /> {details.sku}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
          <span style={{
            display: "inline-flex", alignItems: "baseline", gap: 3, background: GRAD_CTA, borderRadius: 8,
            padding: "4px 12px", fontWeight: 900, color: "#fff", fontSize: 13.5, letterSpacing: -0.2,
            fontFamily: "'Syne',sans-serif",
          }}>
            {fmtPrice(product.rate ?? 0)}
            {unitName && <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.85 }}>/{unitName}</span>}
          </span>

          {rating && (
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <Star size={11} fill={T.saffron} color={T.saffron} />
              <span style={{ color: "#B0650A", fontWeight: 800, fontSize: 12, fontFamily: "'Syne',sans-serif" }}>{rating.avg.toFixed(1)}</span>
              <span style={{ color: T.mutedFaint, fontSize: 11 }}>· {rating.count.toLocaleString()} reviews</span>
            </div>
          )}

          {storage && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: storage.color, fontWeight: 700 }}>
              <storage.icon size={12} /> {storage.name}
            </div>
          )}
        </div>

        {/* shelf-life bar — signature element: empties as expiry approaches */}
        {shelf && (
          <div style={{ marginBottom: 8, maxWidth: 260 }}>
            <div style={{ height: 4, borderRadius: 3, background: T.divider, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${shelf.pct}%`, background: shelf.color, borderRadius: 3, transition: "width .3s" }} />
            </div>
            <span style={{ fontSize: 9.5, color: shelf.color, fontWeight: 700, letterSpacing: "0.02em" }}>
              {shelf.label === "Expired" ? "⚠ Expired" : `Best before · ${shelf.label}`}
            </span>
          </div>
        )}

        {/* Status badges */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {product.isPublished ? (
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.09em", background: T.greenPale, color: T.greenMid,
                            padding: "2px 8px", borderRadius: 5, border: "1px solid rgba(61,112,34,.28)" }}>LIVE</span>
          ) : (
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.09em", background: T.offwhite, color: T.mutedFaint,
                            padding: "2px 8px", borderRadius: 5, border: `1px solid ${T.border}` }}>DRAFT</span>
          )}
          <span style={{
            fontSize: 9, fontWeight: 800, letterSpacing: "0.07em",
            background: isLowStock ? T.saffronPale : T.offwhite, color: isLowStock ? T.saffron : T.mutedFaint,
            padding: "2px 8px", borderRadius: 5, border: `1px solid ${isLowStock ? "rgba(217,119,6,.3)" : T.border}`,
            display: "flex", alignItems: "center", gap: 3,
          }}>
            {isLowStock && <AlertTriangle size={9} />}
            {stock} IN STOCK
          </span>
        </div>
      </div>

      {/* 3-dot button */}
      <button onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
        style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: T.mutedFaint, flexShrink: 0 }}>
        <MoreVertical size={18} />
      </button>

      <AnimatePresence>
        {menuOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 49 }} onClick={(e) => { e.stopPropagation(); setMenuOpen(false); }} />
            <motion.div className="gp-menu" initial={{ opacity: 0, scale: 0.92, y: -6 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: -6 }} transition={{ duration: 0.15 }} onClick={(e) => e.stopPropagation()}>
              <button className="gp-menu-item" onClick={() => { setMenuOpen(false); onEdit(); }}>
                <Edit3 size={14} color={T.greenMid} /> Edit Item
              </button>
              <button className="gp-menu-item danger" onClick={() => { setMenuOpen(false); onDelete(); }}>
                <Trash2 size={14} /> Delete
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN — GroceryProducts
═══════════════════════════════════════════════════════════ */
const GroceryProducts: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const authUserId = useSelector((s: RootState) => s.auth.user?.userId);
  const hostId     = userId ?? authUserId ?? "";

  const allProducts     = useSelector((s: RootState) => s.products.products);
  const ratingSummaries = useSelector((s: RootState) => s.productRatings.summaries);

  // Brand/partner doc — brandId is expected to equal hostId (the seller's
  // own userId), same convention used elsewhere in the dashboard
  // (see CommerceOnboarding.tsx: `s.commercePartners.currentPartner`).
  const { currentPartner: partner } = useSelector((s: RootState) => s.commercePartners);
  const isGroceryStore = partner?.categoryId === GROCERY_STORE_CATEGORY_ID;
  const brandName = isGroceryStore ? partner?.name ?? null : null;
  const brandLogo = isGroceryStore ? partner?.image ?? null : null;
  // Header reads "My Groceries" until the brand loads, then "My {Brand} Groceries".
  const headerTitle = brandName ? `My ${brandName} Groceries` : "My Groceries";

  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState<"all" | "low" | "expiring">("all");
  const [toolsOpen, setToolsOpen] = useState(false); // search + filter hidden by default

  useEffect(() => {
    if (!hostId) return;
    dispatch(listenToHostProducts({ hostId }));
    dispatch(fetchAllProductSummaries());
    const unsub = dispatch(listenToCommerceData(hostId));
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch, hostId]);

  /* only this host's grocery-tree listings */
  const groceryOnly = useMemo(
    () => allProducts.filter((p: any) => GROCERY_CATEGORY_IDS.has(p.categoryId)),
    [allProducts]
  );

  const isLow = (p: any) => {
    const d = p.groceryDetails ?? {};
    return (d.reorderThreshold ?? 0) > 0 && (p.stock ?? 0) <= d.reorderThreshold;
  };
  const isExpiringSoon = (p: any) => {
    const shelf = shelfLifeState(p.groceryDetails?.expirationDate);
    return !!shelf && shelf.label !== "Expired" && (shelf.color === T.saffron || shelf.color === T.red);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = groceryOnly;
    if (q) list = list.filter((p: any) => `${p.name ?? ""} ${p.groceryDetails?.brand ?? ""} ${p.groceryDetails?.sku ?? ""}`.toLowerCase().includes(q));
    if (filterMode === "low") list = list.filter(isLow);
    if (filterMode === "expiring") list = list.filter(isExpiringSoon);
    return list;
  }, [groceryOnly, search, filterMode]);

  const handleDelete = (listingId: string) => {
    if (!window.confirm("Remove this item from your grocery shelf permanently?")) return;
    dispatch(deleteProduct(listingId));
  };

  const goToAdd = (listingId?: string) => navigate(listingId ? `/AddGrocery/${listingId}` : "/AddGrocery");

  const publishedCount = groceryOnly.filter((p: any) => p.isPublished).length;
  const draftCount     = groceryOnly.filter((p: any) => !p.isPublished).length;
  const lowStockCount  = groceryOnly.filter(isLow).length;
  const expiringCount  = groceryOnly.filter(isExpiringSoon).length;

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'DM Sans', sans-serif", paddingBottom: 48 }}>
      <style>{CSS}</style>

      {/* ══ PAGE HEADER ══ */}
      <div style={{ background: T.cardBg, borderBottom: `1px solid ${T.border}`, padding: "0 20px 0", boxShadow: "0 2px 16px rgba(20,24,15,0.04)" }}>
        <div style={{ height: 3, background: GRAD, backgroundSize: GRAD_SIZE, animation: "arSpin 5s linear infinite", marginLeft: -20, marginRight: -20 }} />

        <div style={{ paddingTop: 16, paddingBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <BrandAvatar name={brandName ?? "Groceries"} logo={brandLogo} />
              <div style={{ minWidth: 0 }}>
                <h1 style={{
                  fontWeight: 800, fontSize: 22, color: T.ink, letterSpacing: -0.4, lineHeight: 1.1,
                  fontFamily: "'Syne',sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {headerTitle}
                </h1>
                <p style={{ fontSize: 12.5, color: T.muted, marginTop: 2 }}>Manage stock, pricing and shelf life</p>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              {/* Chevron toggle — reveals/hides search + filter */}
              <button
                className="gp-toolstoggle"
                onClick={() => setToolsOpen((v) => !v)}
                title={toolsOpen ? "Hide search & filter" : "Show search & filter"}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: 38, height: 38, borderRadius: 10, background: toolsOpen ? T.greenPale : T.cardBg,
                  border: `1.5px solid ${toolsOpen ? T.greenMid : T.border}`, cursor: "pointer",
                  color: T.greenMid,
                }}
              >
                <ChevronDown size={17} style={{ transform: toolsOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
              </button>

              <motion.button className="gp-add" whileTap={{ scale: 0.96 }} onClick={() => goToAdd()}
                style={{
                  display: "flex", alignItems: "center", gap: 7, background: GRAD_CTA, border: "none", borderRadius: 12,
                  padding: "10px 18px", color: "#fff", fontWeight: 700, fontSize: 13.5, cursor: "pointer",
                  boxShadow: "0 6px 20px rgba(61,112,34,.26)", fontFamily: "'Syne',sans-serif",
                }}>
                <Plus size={15} /> Add Item
              </motion.button>
            </div>
          </div>

          {/* Search + Filter + Stats — all collapsed by default, toggled via chevron above */}
          <AnimatePresence initial={false}>
            {toolsOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0, marginBottom: 0 }}
                animate={{ height: "auto", opacity: 1, marginBottom: 16 }}
                exit={{ height: 0, opacity: 0, marginBottom: 0 }}
                transition={{ duration: 0.2, ease: [0.2, 0, 0.1, 1] }}
                style={{ overflow: "hidden" }}
              >
                <div style={{ display: "flex", gap: 10, paddingTop: 2 }}>
                  <div className="gp-search" style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, background: T.offwhite,
                                                        borderRadius: 12, border: `1.5px solid ${T.border}`, padding: "11px 16px" }}>
                    <Search size={15} color={T.mutedFaint} />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, brand or SKU…"
                      style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 14, color: T.ink }} />
                  </div>
                  <button className="gp-filter" onClick={() => setFilterMode(filterMode === "all" ? "low" : filterMode === "low" ? "expiring" : "all")}
                    style={{
                      display: "flex", alignItems: "center", gap: 7, background: filterMode !== "all" ? T.greenPale : T.cardBg,
                      border: `1.5px solid ${filterMode !== "all" ? T.greenMid : T.border}`, borderRadius: 12, padding: "11px 18px",
                      color: T.greenMid, fontWeight: 700, fontSize: 13, cursor: "pointer", flexShrink: 0,
                    }}>
                    <SlidersHorizontal size={14} /> {filterMode === "all" ? "Filter" : filterMode === "low" ? "Low Stock" : "Expiring"}
                  </button>
                </div>

                {/* Stat cards — slimmed down, hides/shows with search + filter */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))", gap: 8, marginTop: 10, maxWidth: 620 }}>
                  {[
                    { label: "Live",     val: publishedCount, col: T.greenMid, mode: "all" as const },
                    { label: "Drafts",   val: draftCount,     col: T.muted,    mode: "all" as const },
                    { label: "Low Stock",val: lowStockCount,  col: T.saffron,  mode: "low" as const },
                    { label: "Expiring", val: expiringCount,  col: T.red,      mode: "expiring" as const },
                    { label: "Total",    val: groceryOnly.length, col: T.inkMid, mode: "all" as const },
                  ].map((s) => (
                    <button
                      key={s.label}
                      className="gp-stat"
                      onClick={() => setFilterMode(s.mode === "all" ? "all" : (filterMode === s.mode ? "all" : s.mode))}
                      style={{
                        display: "flex", flexDirection: "column", gap: 2, background: T.offwhite,
                        border: `1.5px solid ${filterMode === s.mode && s.mode !== "all" ? s.col : T.border}`,
                        borderRadius: 10, padding: "7px 10px", cursor: s.mode === "all" ? "default" : "pointer", textAlign: "left",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <div style={{ width: 5, height: 5, borderRadius: "50%", background: s.col, flexShrink: 0 }} />
                        <span style={{ fontSize: 10, color: T.muted, fontWeight: 600 }}>{s.label}</span>
                      </div>
                      <span style={{ fontSize: 16, fontWeight: 800, color: T.ink, fontFamily: "'Syne',sans-serif", letterSpacing: -0.3 }}>{s.val}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* ══ LIST ══ */}
      <div style={{ padding: "16px 16px 0" }}>
        {filtered.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: "center", paddingTop: 80 }}>
            <div style={{ width: 78, height: 78, borderRadius: 20, margin: "0 auto 20px", background: GRAD_CTA,
                          display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 10px 28px rgba(61,112,34,.22)" }}>
              <Package size={34} color="#fff" />
            </div>
            <h2 style={{ fontWeight: 800, fontSize: 20, color: T.ink, marginBottom: 8, fontFamily: "'Syne',sans-serif" }}>
              {groceryOnly.length === 0 ? "No grocery items yet" : "No results found"}
            </h2>
            <p style={{ color: T.muted, marginBottom: 28, fontSize: 14, lineHeight: 1.7, maxWidth: 280, margin: "0 auto 28px" }}>
              {groceryOnly.length === 0
                ? "Add your first item to start stocking your grocery shelf."
                : "Try a different search or clear the filter."}
            </p>
            {groceryOnly.length === 0 && (
              <motion.button whileTap={{ scale: 0.96 }} onClick={() => goToAdd()}
                style={{ padding: "13px 32px", borderRadius: 12, border: "none", background: GRAD_CTA, color: "#fff",
                          fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'Syne',sans-serif",
                          boxShadow: "0 6px 20px rgba(61,112,34,.26)" }}>
                + Add First Item
              </motion.button>
            )}
          </motion.div>
        ) : (
          <AnimatePresence>
            {filtered.map((product: Product) => {
              const sum    = ratingSummaries[product.listingId];
              const rating = sum && sum.totalReviews > 0 ? { avg: sum.avgRating, count: sum.totalReviews } : null;
              return (
                <GroceryRow
                  key={product.listingId}
                  product={product}
                  rating={rating}
                  onEdit={() => goToAdd(product.listingId)}
                  onDelete={() => handleDelete(product.listingId)}
                />
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default GroceryProducts;